import {FeatureTranslationsLoader} from '@hconnect/common/localization-loaders'
import {ServiceUnavailableError} from '@hm/fundamental'
import {usePlantFromHierarchyId} from '@hproduce/suite-core'
import {ErrorBoundary} from 'react-error-boundary'
import {Outlet, useParams, type RouteObject} from 'react-router'

import {RiskSurveyOverviewContainer, RiskSurveyPlantContainer} from '../containers'
import {useRiskSurveyTranslation} from '../hooks'
import {riskSurveyFeatureTranslations} from '../localization'
import {RISK_SURVEY_OVERVIEW} from './routes'

const ErrorFallback = ({resetErrorBoundary}: {resetErrorBoundary: () => void}) => {
  const {t} = useRiskSurveyTranslation()
  return (
    <ServiceUnavailableError
      title={t('errors.title')}
      description={t('errors.description')}
      refreshText={t('errors.refreshAction')}
      onRefresh={resetErrorBoundary}
    />
  )
}

/** Suite adapter — resolves upmId from hierarchyId */
const SuiteRiskSurveyPlant = () => {
  const upmPlant = usePlantFromHierarchyId()
  if (!upmPlant) return null
  return <RiskSurveyPlantContainer upmId={upmPlant.upmId} />
}

/** HProduce adapter — resolves upmId from route param */
const HProduceRiskSurveyPlant = () => {
  const {upmId} = useParams<{upmId: string}>()
  if (!upmId) throw new Error('upmId is missing')
  return <RiskSurveyPlantContainer upmId={upmId} onBack={() => window.history.back()} />
}

/** Suite route tree — uses hierarchyId from Suite's PlantSelector */
export const riskSurveyRoutes: RouteObject = {
  path: '*',
  element: (
    <FeatureTranslationsLoader translations={riskSurveyFeatureTranslations}>
      <Outlet />
    </FeatureTranslationsLoader>
  ),
  children: [
    {
      index: true,
      element: (
        <ErrorBoundary FallbackComponent={ErrorFallback}>
          <SuiteRiskSurveyPlant />
        </ErrorBoundary>
      )
    }
  ]
}

/** HProduce route — overview page */
export const riskSurveyOverviewRoute: RouteObject = {
  path: '',
  element: (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <RiskSurveyOverviewContainer />
    </ErrorBoundary>
  )
}

/** HProduce route — plant detail page */
export const riskSurveyPlantRoute: RouteObject = {
  path: 'plant/:upmId',
  element: (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <HProduceRiskSurveyPlant />
    </ErrorBoundary>
  )
}

/** HProduce combined route — overview + plant detail */
export const hproduceRiskSurveyRoutes: RouteObject = {
  path: '',
  children: [riskSurveyOverviewRoute, riskSurveyPlantRoute]
}
